/**
 * Centralized Google Analytics service for LullabAI.
 *
 * Uses @capacitor-firebase/analytics on native platforms (iOS/Android)
 * and the Firebase JS SDK on web. All analytics calls are fire-and-forget;
 * failures are logged but never block the UI.
 */

import { Capacitor } from '@capacitor/core';

// Lazy-loaded references
let nativeAnalytics: typeof import('@capacitor-firebase/analytics').FirebaseAnalytics | null = null;
let webAnalytics: import('firebase/analytics').Analytics | null = null;
let initialized = false;

/**
 * Initialise analytics. Call once at app startup.
 */
export async function initAnalytics(): Promise<void> {
  if (initialized) return;

  try {
    if (Capacitor.isNativePlatform()) {
      const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');
      nativeAnalytics = FirebaseAnalytics;
      await FirebaseAnalytics.setEnabled({ enabled: true });
    } else {
      // Web: use Firebase JS SDK
      const { getAnalytics, isSupported } = await import('firebase/analytics');
      const { app } = await import('@/lib/firebase');
      const supported = await isSupported();
      if (supported) {
        webAnalytics = getAnalytics(app);
      }
    }
    initialized = true;
  } catch (error) {
    console.warn('[Analytics] Failed to initialise:', error);
  }
}

// ---------------------------------------------------------------------------
// User identification
// ---------------------------------------------------------------------------

/**
 * Set the Firebase user ID for attribution.
 */
export async function setAnalyticsUserId(userId: string): Promise<void> {
  try {
    if (nativeAnalytics) {
      await nativeAnalytics.setUserId({ userId });
    } else if (webAnalytics) {
      const { setUserId } = await import('firebase/analytics');
      setUserId(webAnalytics, userId);
    }
  } catch (error) {
    console.warn('[Analytics] setUserId failed:', error);
  }
}

/**
 * Clear the user ID (e.g. on logout).
 */
export async function clearAnalyticsUserId(): Promise<void> {
  try {
    if (nativeAnalytics) {
      await nativeAnalytics.setUserId({ userId: null as any });
    } else if (webAnalytics) {
      const { setUserId } = await import('firebase/analytics');
      setUserId(webAnalytics, null as any);
    }
  } catch (error) {
    console.warn('[Analytics] clearUserId failed:', error);
  }
}

// ---------------------------------------------------------------------------
// User properties
// ---------------------------------------------------------------------------

/**
 * Set a custom user property for segmentation.
 */
export async function setAnalyticsUserProperty(key: string, value: string): Promise<void> {
  try {
    if (nativeAnalytics) {
      await nativeAnalytics.setUserProperty({ key, value });
    } else if (webAnalytics) {
      const { setUserProperties } = await import('firebase/analytics');
      setUserProperties(webAnalytics, { [key]: value });
    }
  } catch (error) {
    console.warn('[Analytics] setUserProperty failed:', error);
  }
}

// ---------------------------------------------------------------------------
// Screen tracking
// ---------------------------------------------------------------------------

/**
 * Log a screen view event.
 */
export async function trackScreenView(screenName: string): Promise<void> {
  try {
    if (nativeAnalytics) {
      await nativeAnalytics.setCurrentScreen({ screenName });
    } else if (webAnalytics) {
      const { logEvent: webLogEvent } = await import('firebase/analytics');
      webLogEvent(webAnalytics, 'screen_view' as string, { firebase_screen: screenName });
    }
  } catch (error) {
    console.warn('[Analytics] trackScreenView failed:', error);
  }
}

// ---------------------------------------------------------------------------
// Custom events
// ---------------------------------------------------------------------------

/**
 * Log a custom analytics event.
 */
export async function trackEvent(
  name: string,
  params?: Record<string, string | number | boolean>,
): Promise<void> {
  try {
    if (nativeAnalytics) {
      await nativeAnalytics.logEvent({ name, params: params ?? {} });
    } else if (webAnalytics) {
      const { logEvent: webLogEvent } = await import('firebase/analytics');
      webLogEvent(webAnalytics, name as string, params);
    }
  } catch (error) {
    console.warn(`[Analytics] trackEvent(${name}) failed:`, error);
  }
}

// ---------------------------------------------------------------------------
// Convenience helpers for common events
// ---------------------------------------------------------------------------

export const AnalyticsEvents = {
  // Auth
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
  LOGOUT: 'logout',

  // Onboarding
  ONBOARDING_START: 'onboarding_start',
  ONBOARDING_COMPLETE: 'onboarding_complete',

  // Child profiles
  CHILD_PROFILE_CREATED: 'child_profile_created',
  CHILD_PROFILE_EDITED: 'child_profile_edited',

  // Sleep events
  SLEEP_EVENT_LOGGED: 'sleep_event_logged',
  SLEEP_EVENT_EDITED: 'sleep_event_edited',
  SLEEP_EVENT_DELETED: 'sleep_event_deleted',

  // Navigation
  TAB_CHANGED: 'tab_changed',

  // AI Coach
  AI_COACH_MESSAGE_SENT: 'ai_coach_message_sent',
  AI_COACH_RESPONSE_RECEIVED: 'ai_coach_response_received',
  AI_COACH_RESPONSE_FAILED: 'ai_coach_response_failed',
  AI_COACH_CONVERSATION_CLEARED: 'ai_coach_conversation_cleared',
  AI_COACH_PROMPT_GENERATED: 'ai_coach_prompt_generated',
  AI_COACH_GOAL_APPLIED: 'ai_coach_goal_applied',
  AI_COACH_BOOKMARK_TOGGLED: 'ai_coach_bookmark_toggled',

  // Subscriptions
  SUBSCRIPTION_DIALOG_OPENED: 'subscription_dialog_opened',
  SUBSCRIPTION_PURCHASE_STARTED: 'subscription_purchase_started',
  SUBSCRIPTION_PURCHASE_COMPLETED: 'subscription_purchase_completed',
  SUBSCRIPTION_PURCHASE_FAILED: 'subscription_purchase_failed',
  SUBSCRIPTION_RESTORED: 'subscription_restored',
  FREE_TRIAL_EXHAUSTED: 'free_trial_exhausted',

  // Goals
  GOAL_CREATED: 'goal_created',
  GOAL_UPDATED: 'goal_updated',

  // Settings
  THEME_CHANGED: 'theme_changed',
  RATE_APP_CLICKED: 'rate_app_clicked',
  CONTACT_SUPPORT_CLICKED: 'contact_support_clicked',
  DELETE_ACCOUNT_CONFIRMED: 'delete_account_confirmed',

  // Coach tips
  COACH_TIPS_OPENED: 'coach_tips_opened',
} as const;
